import { useMutation, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Search } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { useTranslate } from 'shared/lib/i18n'
import {
	ROLE_BINDING,
	STAFF_ROLES,
	isLocationBound,
	placesFor,
	type Role,
} from 'shared/lib/roles'
import type { RouterOutputs } from 'shared/api'
import { useAuthStore } from 'shared/store/auth'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { FacetedFilter } from 'shared/components/FacetedFilter'
import { PageFrame } from 'shared/components/page-frame'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { useDataTable } from 'shared/hooks/use-data-table'

/**
 * Staff accounts and access (spec §7, ADR-0029). An administrator creates an
 * account of any of the six roles, binds it to its place, edits it and deactivates
 * it — all data, no deploy. Access is decided by the role, never by a username.
 *
 * The accounts are one table with a search and a role filter over it; «Новый
 * сотрудник» and «Изменить» are dialogs; switching an account off asks first.
 *
 * This whole section is admin-only, and that rule lives in the role → sections map
 * (`app/ui/routes.tsx`); the page does not re-derive it. The backend is the real
 * guard — anyone else reaching these procedures is refused there. The (role,
 * place) invariant is likewise the backend's: an `operator` is bound to one Point,
 * a `hub` worker to a hub, every other role to nothing; the forms only shape the
 * input to match, and offer each bound role only the places of its type
 * (`placesFor`).
 */

// The staff record as the section shows it (CONTEXT — Operator is our staff).
type Operator = RouterOutputs['staff']['List'][number]
type Location = RouterOutputs['locations']['List'][number]

export function Staff() {
	const { t } = useTranslate()
	const [creating, setCreating] = useState(false)
	const [editing, setEditing] = useState<Operator | null>(null)
	const [switchingOff, setSwitchingOff] = useState<Operator | null>(null)
	const [search, setSearch] = useState('')
	const [roles, setRoles] = useState<string[] | undefined>()

	const staffQuery = useQuery(trpc.staff.List.queryOptions())
	const locationsQuery = useQuery(trpc.locations.List.queryOptions())
	const locations = locationsQuery.data ?? []

	const setActiveMutation = useMutation({
		...trpc.staff.SetActive.mutationOptions(),
		onSuccess: async (updated) => {
			setSwitchingOff(null)
			await staffQuery.refetch()
			toast.success(updated.isActive ? t('staff.reactivated') : t('staff.deactivated'))
		},
		onError: (error) => toast.error(error.message || t('staff.updateFailed')),
	})

	const needle = search.trim().toLowerCase()
	const rows = useMemo(
		() =>
			(staffQuery.data ?? []).filter(
				(a) =>
					(!roles?.length || roles.includes(a.role)) &&
					(!needle ||
						a.name.toLowerCase().includes(needle) ||
						a.username.toLowerCase().includes(needle) ||
						(a.locationCode ?? '').toLowerCase().includes(needle)),
			),
		[staffQuery.data, roles, needle],
	)

	const columns: ColumnDef<Operator>[] = [
		{ id: 'name', header: t('staff.colName'), cell: ({ row }) => <span className='font-medium'>{row.original.name}</span> },
		{
			id: 'username',
			header: t('staff.colUsername'),
			cell: ({ row }) => <span className='font-mono text-muted-foreground'>{row.original.username}</span>,
		},
		{
			id: 'role',
			header: t('staff.colRole'),
			cell: ({ row }) => <span data-testid='staff-row-role'>{t(`roles.${row.original.role}`)}</span>,
		},
		{
			id: 'location',
			header: t('staff.colLocation'),
			cell: ({ row: { original: a } }) => (
				<span data-testid='staff-row-location' className={a.locationId ? 'font-mono' : 'text-muted-foreground'}>
					{a.locationId ? (a.locationCode ?? '—') : t('staff.noLocation')}
				</span>
			),
		},
		{
			id: 'status',
			header: t('staff.colStatus'),
			cell: ({ row }) =>
				row.original.isActive ? (
					<Badge variant='outline'>{t('staff.active')}</Badge>
				) : (
					<Badge variant='secondary'>{t('staff.inactive')}</Badge>
				),
		},
		{
			id: 'actions',
			header: '',
			meta: { cellClassName: 'w-px text-right' },
			cell: ({ row: { original: account } }) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant='ghost'
							size='icon-sm'
							aria-label={t('staff.actions')}
							data-testid={`staff-menu-${account.username}`}
						>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem onSelect={() => setEditing(account)} data-testid='staff-edit'>
							{t('staff.edit')}
						</DropdownMenuItem>
						{account.isActive ? (
							<DropdownMenuItem
								variant='destructive'
								onSelect={() => setSwitchingOff(account)}
								data-testid='staff-deactivate'
							>
								{t('staff.deactivate')}
							</DropdownMenuItem>
						) : (
							<DropdownMenuItem
								disabled={setActiveMutation.isPending}
								onSelect={() => setActiveMutation.mutate({ id: account._id, isActive: true })}
								data-testid='staff-reactivate'
							>
								{t('staff.reactivate')}
							</DropdownMenuItem>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		// The rows are filtered here: a new list must not reset the page in a loop.
		autoResetPageIndex: false,
		getRowId: (a) => a._id,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<PageFrame
			title='staff.title'
			actions={
				<Button data-testid='staff-new' onClick={() => setCreating(true)}>
					<Plus />
					{t('staff.new')}
				</Button>
			}
		>
			<div className='flex flex-col gap-3'>
				<div className='flex flex-wrap items-center gap-2'>
					<div className='relative w-64'>
						<Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' />
						<Input
							className='h-8 pl-8'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t('staff.search')}
							aria-label={t('staff.search')}
							data-testid='staff-search'
						/>
					</div>
					<FacetedFilter
						title={t('staff.colRole')}
						values={roles}
						setValues={setRoles}
						options={STAFF_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
					/>
				</div>
				{staffQuery.isError ? (
					<p className='text-sm text-destructive'>{staffQuery.error.message}</p>
				) : staffQuery.isLoading ? (
					<p className='text-sm text-muted-foreground'>{t('staff.loading')}</p>
				) : (
					<DataTable
						table={table}
						withPagination={false}
						emptyText={t('staff.empty')}
						row={{ attributes: (a) => ({ 'data-testid': `staff-row-${a.username}`, 'data-active': a.isActive }) }}
					/>
				)}
			</div>

			<CreateStaffDialog
				open={creating}
				onOpenChange={setCreating}
				locations={locations}
				onCreated={() => staffQuery.refetch()}
			/>
			<EditStaffDialog
				account={editing}
				locations={locations}
				onClose={() => setEditing(null)}
				onSaved={() => staffQuery.refetch()}
			/>
			<AlertDialog
				open={switchingOff !== null}
				onOpenChange={(open) => !open && !setActiveMutation.isPending && setSwitchingOff(null)}
			>
				<AlertDialogContent data-testid='staff-deactivate-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('staff.deactivateAsk', { name: switchingOff?.name ?? '' })}
						</AlertDialogTitle>
						<AlertDialogDescription>{t('staff.deactivateHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={setActiveMutation.isPending}>{t('staff.cancel')}</AlertDialogCancel>
						<Button
							variant='destructive'
							data-testid='staff-deactivate-confirm'
							disabled={setActiveMutation.isPending}
							onClick={() => switchingOff && setActiveMutation.mutate({ id: switchingOff._id, isActive: false })}
						>
							{t('staff.deactivate')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageFrame>
	)
}

function RoleSelect({
	value,
	onChange,
	testId,
	id,
}: {
	value: Role
	onChange: (role: Role) => void
	testId?: string
	id?: string
}) {
	const { t } = useTranslate()
	// Only a superadmin gives the superadmin role; the server refuses it to anyone else.
	const giver = useAuthStore((s) => s.user.role)
	const offered = STAFF_ROLES.filter((r) => r !== 'superadmin' || giver === 'superadmin')
	return (
		<Select value={value} onValueChange={(v) => onChange(v as Role)}>
			<SelectTrigger id={id} className='w-full' data-testid={testId}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{offered.map((r) => (
					<SelectItem key={r} value={r}>
						{t(`roles.${r}`)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

/**
 * The place a bound role works at: a Point for an operator, a hub for a hub
 * worker, offered only among the places of that type. A global role has no field.
 */
function BindingField({
	role,
	value,
	onChange,
	locations,
	testId,
}: {
	role: Role
	value: string
	onChange: (id: string) => void
	locations: Location[]
	testId?: string
}) {
	const { t } = useTranslate()
	const type = ROLE_BINDING[role]
	if (!type) return null
	const isHub = type === 'hub'
	return (
		<div className='grid gap-1.5'>
			<Label>{t(isHub ? 'staff.bindHub' : 'staff.bindPoint')}</Label>
			<Select value={value} onValueChange={onChange}>
				<SelectTrigger className='w-full' data-testid={testId}>
					<SelectValue placeholder={t(isHub ? 'staff.hubPlaceholder' : 'staff.pointPlaceholder')} />
				</SelectTrigger>
				<SelectContent>
					{placesFor(role, locations).map((l) => (
						<SelectItem key={l._id} value={l._id}>
							{l.id}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}

function CreateStaffDialog({
	open,
	onOpenChange,
	locations,
	onCreated,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	locations: Location[]
	onCreated: () => void
}) {
	const { t } = useTranslate()
	const [name, setName] = useState('')
	const [username, setUsername] = useState('')
	const [password, setPassword] = useState('')
	const [role, setRole] = useState<Role>('operator')
	const [locationId, setLocationId] = useState('')

	const createMutation = useMutation({
		...trpc.staff.Create.mutationOptions(),
		onSuccess: () => {
			setName('')
			setUsername('')
			setPassword('')
			setLocationId('')
			onOpenChange(false)
			onCreated()
			toast.success(t('staff.created'))
		},
		onError: (error) => toast.error(error.message || t('staff.createFailed')),
	})

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		// A bound role sends its place; a global role sends none, matching the
		// backend invariant (ADR-0029).
		createMutation.mutate(
			isLocationBound(role)
				? { name, username, password, role, locationId }
				: { name, username, password, role },
		)
	}

	const canSubmit =
		!!name.trim() &&
		!!username.trim() &&
		password.length >= 8 &&
		(!isLocationBound(role) || !!locationId)

	// A place chosen for one role is never carried over to another: a Point is no
	// hub worker's place.
	const changeRole = (next: Role) => {
		setRole(next)
		setLocationId('')
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='staff-create-dialog'>
				<DialogHeader>
					<DialogTitle>{t('staff.new')}</DialogTitle>
				</DialogHeader>
				<form className='grid gap-4' onSubmit={handleSubmit}>
					<div className='grid gap-1.5'>
						<Label htmlFor='staff-name'>{t('staff.name')}</Label>
						<Input id='staff-name' value={name} onChange={(e) => setName(e.target.value)} required />
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<div className='grid gap-1.5'>
							<Label htmlFor='staff-username'>{t('staff.username')}</Label>
							<Input id='staff-username' value={username} onChange={(e) => setUsername(e.target.value)} required />
						</div>
						<div className='grid gap-1.5'>
							<Label htmlFor='staff-password'>{t('staff.password')}</Label>
							<Input
								id='staff-password'
								type='password'
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								minLength={8}
								required
							/>
						</div>
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<div className='grid gap-1.5'>
							<Label htmlFor='staff-role'>{t('staff.role')}</Label>
							<RoleSelect id='staff-role' value={role} onChange={changeRole} testId='staff-role' />
						</div>
						<BindingField
							role={role}
							value={locationId}
							onChange={setLocationId}
							locations={locations}
							testId='staff-location'
						/>
					</div>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
							{t('staff.cancel')}
						</Button>
						<Button disabled={createMutation.isPending || !canSubmit} data-testid='staff-create'>
							{t('staff.create')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

function EditStaffDialog({
	account,
	locations,
	onClose,
	onSaved,
}: {
	account: Operator | null
	locations: Location[]
	onClose: () => void
	onSaved: () => void
}) {
	const { t } = useTranslate()
	const [name, setName] = useState('')
	const [role, setRole] = useState<Role>('operator')
	const [locationId, setLocationId] = useState('')

	// Reset the draft to the record whenever another one is opened, so a cancelled
	// edit never leaks into the next one.
	const [seeded, setSeeded] = useState<Operator | null>(null)
	if (account !== seeded) {
		setSeeded(account)
		if (account) {
			setName(account.name)
			setRole(account.role)
			setLocationId(account.locationId ?? '')
		}
	}

	const updateMutation = useMutation({
		...trpc.staff.Update.mutationOptions(),
		onSuccess: () => {
			onSaved()
			onClose()
			toast.success(t('staff.updated'))
		},
		onError: (error) => toast.error(error.message || t('staff.updateFailed')),
	})

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (!account) return
		// A bound role carries its place; a global role carries none — the backend
		// clears any stale binding on the change (ADR-0029).
		updateMutation.mutate(
			isLocationBound(role)
				? { id: account._id, name, role, locationId }
				: { id: account._id, name, role },
		)
	}

	const canSave = !!name.trim() && (!isLocationBound(role) || !!locationId)

	const changeRole = (next: Role) => {
		setRole(next)
		setLocationId(next === account?.role ? (account.locationId ?? '') : '')
	}

	return (
		<Dialog open={account !== null} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='staff-edit-dialog'>
				<DialogHeader>
					<DialogTitle>{t('staff.editTitle')}</DialogTitle>
				</DialogHeader>
				<form className='grid gap-4' onSubmit={handleSubmit}>
					<div className='grid gap-1.5'>
						<Label htmlFor='edit-name'>{t('staff.name')}</Label>
						<Input id='edit-name' value={name} onChange={(e) => setName(e.target.value)} required />
					</div>
					<div className='grid grid-cols-2 gap-4'>
						<div className='grid gap-1.5'>
							<Label htmlFor='edit-role'>{t('staff.role')}</Label>
							<RoleSelect id='edit-role' value={role} onChange={changeRole} />
						</div>
						<BindingField role={role} value={locationId} onChange={setLocationId} locations={locations} />
					</div>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={onClose}>
							{t('staff.cancel')}
						</Button>
						<Button disabled={updateMutation.isPending || !canSave} data-testid='staff-save'>
							{t('staff.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
